// <s-card-quick-buy> lets shoppers add a card's product to the cart without
// opening it. Tablet/desktop: pick a size ([data-size]) in the hover panel and
// press [data-quick-add]. Phones: [data-qb-open] either opens the shared bottom
// sheet (dialog[data-qb-sheet], data-mobile-mode="sheet") or reveals the same
// panel over the photo (data-mobile-mode="overlay", toggles [data-open]).
// The card color is its pressed swatch ([data-swatch][data-value]) or the
// default variant's. Adds via Shopify.actions.updateCart and opens the drawer.

function readData(root) {
  try {
    return JSON.parse(root.querySelector('script[data-variants]')?.textContent || '{}');
  } catch {
    return {};
  }
}

function matches(variant, colorIndex, color, sizeIndex, size) {
  if (colorIndex >= 0 && color !== null && variant.options[colorIndex] !== color) return false;
  if (sizeIndex >= 0 && size !== null && variant.options[sizeIndex] !== size) return false;
  return true;
}

// The drawer covers the card's inline message (and the phone sheet has none),
// so add failures are shown as a toast above the drawer (s-notification).
function toastError(message) {
  if (!message) return;
  document.dispatchEvent(new CustomEvent('s-notification:add', { detail: { message, variant: 'error' } }));
}

// Opens the drawer right away (dimmed) so the tap feels instant, then adds the line.
async function addToCart(variantId) {
  const drawerItems = document.getElementById('cart-drawer-items');
  drawerItems?.setAttribute('aria-busy', 'true');
  window.Shopify.actions.openCart?.();
  try {
    const result = await window.Shopify.actions.updateCart(
      { lines: [{ merchandiseId: String(variantId), quantity: 1 }] },
      { event: { context: 'product' } }
    );
    const message = result?.userErrors?.[0]?.message || '';
    toastError(message);
    return message;
  } catch (error) {
    console.error('[quick-buy] add to cart failed', error, variantId);
    const message = error?.cause?.userErrors?.[0]?.message || error?.message || 'Não foi possível adicionar ao carrinho';
    toastError(message);
    return message;
  } finally {
    drawerItems?.removeAttribute('aria-busy');
  }
}

// Shared phone bottom sheet; one instance per page, refilled for each card.
const sheet = {
  dialog: null,
  data: null,
  owner: null,
  color: null,
  size: null,

  bind(dialog) {
    if (this.dialog === dialog) return;
    this.dialog = dialog;
    dialog.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-qb-sheet-close]')) {
        dialog.close();
        return;
      }
      const colorButton = event.target.closest('[data-qb-color]');
      const sizeButton = event.target.closest('[data-qb-size]');
      if (colorButton) {
        this.color = colorButton.dataset.qbColor;
        if (this.size !== null && !this._available(this.color, this.size)) this.size = null;
        this.render();
      }
      if (sizeButton && !sizeButton.disabled) {
        this.size = sizeButton.dataset.qbSize;
        this._error('');
        this.render();
      }
      if (event.target.closest('[data-qb-add]')) this.add();
    });
  },

  open(owner, data, color) {
    this.owner = owner;
    this.data = data;
    this.color = color;
    this.size = null;
    const d = this.dialog;
    // Sign Kids cards ask for the blue add button (see .button-primary.kids-add-button in config/styles.css).
    d.dataset.buttonStyle = owner.dataset.buttonStyle || '';
    d.querySelector('[data-qb-title]').textContent = data.title || '';
    this._error('');
    this._buildColors();
    this._buildSizes();
    this.render();
    d.showModal();
  },

  _available(color, size) {
    const { colorIndex, sizeIndex } = this.owner;
    return this.data.variants.some((v) => v.available && matches(v, colorIndex, color, sizeIndex, size));
  },

  _variant() {
    const { colorIndex, sizeIndex } = this.owner;
    const list = this.data.variants;
    return list.find((v) => v.available && matches(v, colorIndex, this.color, sizeIndex, this.size))
      || list.find((v) => matches(v, colorIndex, this.color, sizeIndex, this.size))
      || list[0];
  },

  _buildColors() {
    const group = this.dialog.querySelector('[data-qb-color-group]');
    const box = this.dialog.querySelector('[data-qb-colors]');
    const proto = this.dialog.querySelector('template[data-qb-color-proto]');
    box.replaceChildren();
    const colors = this.data.colors || [];
    group.hidden = colors.length === 0;
    colors.forEach((color) => {
      const button = proto.content.firstElementChild.cloneNode(true);
      button.dataset.qbColor = color.value;
      button.setAttribute('aria-label', color.value);
      button.title = color.value;
      button.querySelector('span').style.backgroundColor = color.hex;
      box.append(button);
    });
  },

  _buildSizes() {
    const group = this.dialog.querySelector('[data-qb-size-group]');
    const box = this.dialog.querySelector('[data-qb-sizes]');
    const proto = this.dialog.querySelector('template[data-qb-size-proto]');
    box.replaceChildren();
    const sizes = this.data.sizes || [];
    group.hidden = sizes.length === 0;
    sizes.forEach((size) => {
      const button = proto.content.firstElementChild.cloneNode(true);
      button.dataset.qbSize = size;
      button.textContent = size;
      box.append(button);
    });
  },

  render() {
    const d = this.dialog;
    d.querySelectorAll('[data-qb-color]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.qbColor === this.color)));
    d.querySelectorAll('[data-qb-size]').forEach((b) => {
      b.disabled = !this._available(this.color, b.dataset.qbSize);
      b.setAttribute('aria-pressed', String(b.dataset.qbSize === this.size));
    });
    const colorName = d.querySelector('[data-qb-color-name]');
    if (colorName) colorName.textContent = this.color || '';
    const colorInfo = (this.data.colors || []).find((c) => c.value === this.color);
    const image = d.querySelector('[data-qb-image]');
    const src = colorInfo?.image || this.data.image;
    if (src && image.getAttribute('src') !== src) image.src = src;
    const variant = this._variant();
    d.querySelector('[data-qb-price]').textContent = variant?.price || '';
    d.querySelector('[data-qb-compare]').textContent = variant?.compare || '';
  },

  _error(text) {
    const error = this.dialog.querySelector('[data-qb-error]');
    error.textContent = text;
    error.hidden = !text;
  },

  async add() {
    const hasSizes = (this.data.sizes || []).length > 0;
    if (hasSizes && this.size === null) {
      this._error(this.owner.dataset.chooseMessage || '');
      return;
    }
    const { colorIndex, sizeIndex } = this.owner;
    const variant = this.data.variants.find((v) => v.available && matches(v, colorIndex, this.color, sizeIndex, this.size));
    if (!variant) return;
    this.dialog.close();
    if (!window.Shopify?.actions?.updateCart) {
      const link = this.owner.closest('article')?.querySelector('a[href]');
      if (link) window.location.assign(link.href);
      return;
    }
    await addToCart(variant.id);
  },
};

class SCardQuickBuy extends HTMLElement {
  #ac;
  #data = {};
  #swatchObserver;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    this.#data = readData(this);
    this.#data.variants ||= [];

    this.addEventListener('click', (event) => {
      const open = event.target.closest('[data-qb-open]');
      const close = event.target.closest('[data-qb-close]');
      const size = event.target.closest('[data-size]');
      const add = event.target.closest('[data-quick-add]');
      if (!open && !close && !size && !add) return;
      event.preventDefault();
      event.stopPropagation();
      if (open) this.#openMobile();
      if (close) this.toggleAttribute('data-open', false);
      if (size && !size.disabled) this.#pickSize(size);
      if (add) this.#add(add);
    }, { signal });

    // Tapping anywhere else closes an open overlay panel.
    document.addEventListener('click', (event) => {
      if (this.hasAttribute('data-open') && !this.contains(event.target)) this.toggleAttribute('data-open', false);
    }, { signal, capture: true });

    const card = this.closest('article');
    card?.addEventListener('pointerenter', () => this.#refresh(), { signal });
    const swatches = card?.querySelector('s-card-swatches');
    if (swatches) {
      this.#swatchObserver = new MutationObserver(() => this.#refresh());
      this.#swatchObserver.observe(swatches, { subtree: true, attributes: true, attributeFilter: ['aria-pressed'] });
    }
    this.#refresh();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#swatchObserver?.disconnect();
  }

  get sizeIndex() {
    return Number(this.dataset.sizeIndex);
  }

  get colorIndex() {
    return Number(this.dataset.colorIndex);
  }

  #openMobile() {
    const dialog = document.querySelector('dialog[data-qb-sheet]');
    if (this.dataset.mobileMode === 'sheet' && dialog) {
      sheet.bind(dialog);
      sheet.open(this, this.#data, this.#color());
      return;
    }
    this.#refresh();
    this.toggleAttribute('data-open', true);
  }

  #color() {
    if (this.colorIndex < 0) return null;
    const pressed = this.closest('article')?.querySelector('[data-swatch][aria-pressed="true"]');
    if (pressed?.dataset.value) return pressed.dataset.value;
    const fallback = this.#data.variants.find((variant) => String(variant.id) === this.dataset.defaultVariant);
    return fallback ? fallback.options[this.colorIndex] : null;
  }

  // Disables sizes that are sold out in the current color.
  #refresh() {
    const color = this.#color();
    this.querySelectorAll('[data-size]').forEach((button) => {
      const available = this.#data.variants.some((v) => v.available && matches(v, this.colorIndex, color, this.sizeIndex, button.dataset.size));
      button.disabled = !available;
      if (!available) button.setAttribute('aria-pressed', 'false');
    });
  }

  #pickSize(button) {
    this.querySelectorAll('[data-size]').forEach((other) => {
      other.setAttribute('aria-pressed', String(other === button));
    });
    this.#message('');
  }

  #message(text) {
    const error = this.querySelector('[data-quick-error]');
    if (!error) return;
    error.textContent = text;
    error.hidden = !text;
  }

  #fallback() {
    const link = this.closest('article')?.querySelector('a[href]');
    if (link) window.location.assign(link.href);
  }

  async #add(button) {
    if (button.getAttribute('aria-busy') === 'true') return;
    const hasSizes = this.sizeIndex >= 0 && this.querySelector('[data-size]');
    const size = this.querySelector('[data-size][aria-pressed="true"]')?.dataset.size ?? null;
    if (hasSizes && size === null) {
      this.#message(this.dataset.chooseMessage || '');
      return;
    }
    const color = this.#color();
    const variant = this.#data.variants.find((v) => v.available && matches(v, this.colorIndex, color, this.sizeIndex, size));
    if (!variant || !window.Shopify?.actions?.updateCart) {
      this.#fallback();
      return;
    }

    const label = button.textContent;
    button.setAttribute('aria-busy', 'true');
    button.textContent = this.dataset.addingMessage || label;
    this.#message('');
    this.toggleAttribute('data-open', false);
    const errorMessage = await addToCart(variant.id);
    if (errorMessage) this.#message(errorMessage);
    button.removeAttribute('aria-busy');
    button.textContent = label;
  }
}

if (!customElements.get('s-card-quick-buy')) customElements.define('s-card-quick-buy', SCardQuickBuy);
